import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "./context/AuthContext";
import { SupplierAuthProvider } from "./context/SupplierAuthContext";
import { ThemeProvider } from "./context/ThemeContext";
import { SubscriptionProvider } from "./context/SubscriptionContext";
import { Toaster } from "./components/ui/sonner";
import ProtectedRoute from "./components/ProtectedRoute";
import AdminRoute from "./components/AdminRoute";
import PartnerRoute from "./components/PartnerRoute";
import SupplierRoute from "./components/SupplierRoute";

// Public Pages
import LandingPage from "./pages/LandingPage";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import InviteAcceptPage from "./pages/InviteAcceptPage";
import PrivacyPage from "./pages/PrivacyPage";
import TermsPage from "./pages/TermsPage";

// Customer Pages
import DashboardPage from "./pages/DashboardPage";
import RisksPage from "./pages/RisksPage";
import ActionDetailPage from "./pages/ActionDetailPage";
import LeveranciersPage from "./pages/LeveranciersPage";
import SettingsPage from "./pages/SettingsPage";
import PeoplePage from "./pages/PeoplePage";
import ProcessPage from "./pages/ProcessPage";
import TechnologyPage from "./pages/TechnologyPage";
import FinancialPage from "./pages/FinancialPage";
import ReAssessmentPage from "./pages/ReAssessmentPage";

// GGRC Pages
import AssessmentPage from "./pages/AssessmentPage";
import AssetsPage from "./pages/AssetsPage";
import VulnerabilitiesPage from "./pages/VulnerabilitiesPage";
import ThreatsPage from "./pages/ThreatsPage";
import MeasuresPage from "./pages/MeasuresPage";
import GGRCChainPage from "./pages/GGRCChainPage";
import StrategiePage from "./pages/StrategiePage";
import ModulesPage from "./pages/ModulesPage";

// Beheer Pages
import OrganizationPage from "./pages/OrganizationPage";
import InvoicesPage from "./pages/InvoicesPage";
import SubscriptionPage from "./pages/customer/SubscriptionPage";
import MaturityPage from "./pages/MaturityPage";
import ContactsPage from "./pages/ContactsPage";
import RACIPage from "./pages/RACIPage";
import TeamsPage from "./pages/TeamsPage";

// Section Home Pages
import BeoordelenHomePage from "./pages/BeoordelenHomePage";
import RisicosHomePage from "./pages/RisicosHomePage";
import VerbeterenHomePage from "./pages/VerbeterenHomePage";
import OrganisatieHomePage from "./pages/OrganisatieHomePage";

// Admin Pages
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminOrganizations from "./pages/admin/AdminOrganizations";
import AdminOrganizationDetail from "./pages/admin/AdminOrganizationDetail";
import AdminLeveranciers from "./pages/admin/AdminLeveranciers";
import AdminQuotes from "./pages/admin/AdminQuotes";
import AdminEmail from "./pages/admin/AdminEmail";
import AdminFrameworks from "./pages/admin/AdminFrameworks";
import AdminMappings from "./pages/admin/AdminMappings";
import AdminUsers from "./pages/admin/AdminUsers";
import AdminAanmeldingen from "./pages/admin/AdminAanmeldingen";
import AdminThreats from "./pages/admin/AdminThreats";
import AdminSupplierManagement from "./pages/admin/AdminSupplierManagement";
import AdminModulesPage from "./pages/admin/AdminModulesPage";
import AdminEmailTemplates from "./pages/admin/AdminEmailTemplates";
import TierManagementPage from "./pages/admin/TierManagementPage";
import PaymentConfigPage from "./pages/admin/PaymentConfigPage";

// Partner Pages
import PartnerDashboard from "./pages/partner/PartnerDashboard";
import PartnerProfile from "./pages/partner/PartnerProfile";
import PartnerClients from "./pages/partner/PartnerClients";
import PartnerQuotes from "./pages/partner/PartnerQuotes";
import PartnerInsights from "./pages/partner/PartnerInsights";

// Supplier Pages
import SupplierLoginPage from "./pages/supplier/SupplierLoginPage";
import SupplierRegisterPage from "./pages/supplier/SupplierRegisterPage";
import SupplierDashboard from "./pages/supplier/SupplierDashboard";
import SupplierLeadsPage from "./pages/supplier/SupplierLeadsPage";
import SupplierProfilePage from "./pages/supplier/SupplierProfilePage";
import SupplierFinancePage from "./pages/supplier/SupplierFinancePage";
import SupplierRatingsPage from "./pages/supplier/SupplierRatingsPage";
import SupplierUsersPage from "./pages/supplier/SupplierUsersPage";
import SupplierCompliancePage from "./pages/supplier/SupplierCompliancePage";
import SupplierSettingsPage from "./pages/supplier/SupplierSettingsPage";

import ErrorBoundary from "./components/ErrorBoundary";
import { ErrorBoundary as ModuleBoundary } from "./components/ui/error-boundary";

// Wrap page with per-module error boundary
const Guarded = ({ name, children }) => (
  <ModuleBoundary name={name}>{children}</ModuleBoundary>
);

import "./App.css";

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <SubscriptionProvider>
        <SupplierAuthProvider>
          <ErrorBoundary>
          <BrowserRouter>
            <Routes>
              {/* Public Routes */}
              <Route path="/" element={<Navigate to="/login" replace />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/register" element={<RegisterPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              <Route path="/invite/accept" element={<InviteAcceptPage />} />
              <Route path="/privacy" element={<PrivacyPage />} />
              <Route path="/terms" element={<TermsPage />} />
              
              {/* Supplier Public Routes */}
              <Route path="/leverancier/login" element={<SupplierLoginPage />} />
              <Route path="/leverancier/register" element={<SupplierRegisterPage />} />
              
              {/* Redirect partner login to leverancier login */}
              <Route path="/partner/login" element={<Navigate to="/leverancier/login" replace />} />
              <Route path="/partner/register" element={<Navigate to="/leverancier/register" replace />} />
            
            {/* Protected Customer Routes */}
            <Route element={<ProtectedRoute />}>
              {/* Home */}
              <Route path="/dashboard" element={<Guarded name="dashboard"><DashboardPage /></Guarded>} />

              {/* Beoordelen */}
              <Route path="/beoordelen" element={<BeoordelenHomePage />} />
              <Route path="/beoordelen/nulmeting" element={<Guarded name="assessment"><AssessmentPage /></Guarded>} />
              <Route path="/beoordelen/volwassenheid" element={<Guarded name="maturity"><MaturityPage /></Guarded>} />
              <Route path="/assessment" element={<Navigate to="/beoordelen" replace />} />
              <Route path="/maturity" element={<Navigate to="/beoordelen/volwassenheid" replace />} />

              {/* Risico's */}
              <Route path="/risicos" element={<RisicosHomePage />} />
              <Route path="/risicos/overzicht" element={<Guarded name="risks"><RisksPage /></Guarded>} />
              <Route path="/risicos/kwetsbaarheden" element={<Guarded name="vulnerabilities"><VulnerabilitiesPage /></Guarded>} />
              <Route path="/risicos/dreigingen" element={<Guarded name="threats"><ThreatsPage /></Guarded>} />
              <Route path="/risicos/systemen" element={<Guarded name="assets"><AssetsPage /></Guarded>} />
              <Route path="/risks" element={<Navigate to="/risicos/overzicht" replace />} />
              <Route path="/risks/:riskType" element={<Navigate to="/risicos/overzicht" replace />} />
              <Route path="/vulnerabilities" element={<Navigate to="/risicos/kwetsbaarheden" replace />} />
              <Route path="/threats" element={<Navigate to="/risicos/dreigingen" replace />} />
              <Route path="/assets" element={<Navigate to="/risicos/systemen" replace />} />

              {/* Verbeteren */}
              <Route path="/verbeteren" element={<VerbeterenHomePage />} />
              <Route path="/verbeteren/maatregelen" element={<Guarded name="measures"><MeasuresPage /></Guarded>} />
              <Route path="/verbeteren/strategie" element={<Guarded name="strategy"><StrategiePage /></Guarded>} />
              <Route path="/verbeteren/financieel" element={<Guarded name="financial"><FinancialPage /></Guarded>} />
              <Route path="/verbeteren/leveranciers" element={<Guarded name="leveranciers"><LeveranciersPage /></Guarded>} />
              <Route path="/measures" element={<Navigate to="/verbeteren/maatregelen" replace />} />
              <Route path="/strategy" element={<Navigate to="/verbeteren/strategie" replace />} />
              <Route path="/financial" element={<Navigate to="/verbeteren/financieel" replace />} />
              <Route path="/leveranciers" element={<Navigate to="/verbeteren/leveranciers" replace />} />

              {/* Organisatie */}
              <Route path="/organisatie" element={<OrganisatieHomePage />} />
              <Route path="/organisatie/gegevens" element={<OrganizationPage />} />
              <Route path="/organisatie/mensen" element={<ContactsPage />} />
              <Route path="/organisatie/verantwoordelijkheden" element={<RACIPage />} />
              <Route path="/organization" element={<Navigate to="/organisatie/gegevens" replace />} />
              <Route path="/contacts" element={<Navigate to="/organisatie/mensen" replace />} />
              <Route path="/teams" element={<Navigate to="/organisatie/mensen" replace />} />
              <Route path="/raci" element={<Navigate to="/organisatie/verantwoordelijkheden" replace />} />

              {/* Account (niet in sidebar, via avatar menu) */}
              <Route path="/subscription" element={<SubscriptionPage />} />
              <Route path="/invoices" element={<InvoicesPage />} />
              <Route path="/settings" element={<SettingsPage />} />

              {/* Overige */}
              <Route path="/people" element={<PeoplePage />} />
              <Route path="/process" element={<ProcessPage />} />
              <Route path="/technology" element={<TechnologyPage />} />
              <Route path="/modules" element={<Navigate to="/subscription" replace />} />
              <Route path="/actions/:actionId" element={<ActionDetailPage />} />
              <Route path="/reassessment" element={<ReAssessmentPage />} />
            </Route>
            
            {/* Protected Admin Routes */}
            <Route element={<AdminRoute />}>
              <Route path="/admin" element={<AdminDashboard />} />
              <Route path="/admin/organizations" element={<AdminOrganizations />} />
              <Route path="/admin/organizations/:orgId" element={<AdminOrganizationDetail />} />
              <Route path="/admin/leveranciers" element={<AdminLeveranciers />} />
              <Route path="/admin/marketplace" element={<AdminSupplierManagement />} />
              <Route path="/admin/quotes" element={<AdminQuotes />} />
              <Route path="/admin/email" element={<AdminEmail />} />
              <Route path="/admin/email-templates" element={<AdminEmailTemplates />} />
              <Route path="/admin/frameworks" element={<AdminFrameworks />} />
              <Route path="/admin/mappings" element={<AdminMappings />} />
              <Route path="/admin/users" element={<AdminUsers />} />
              <Route path="/admin/aanmeldingen" element={<AdminAanmeldingen />} />
              <Route path="/admin/threats" element={<AdminThreats />} />
              <Route path="/admin/modules" element={<AdminModulesPage />} />
              <Route path="/admin/tiers" element={<TierManagementPage />} />
              <Route path="/admin/payment-config" element={<PaymentConfigPage />} />
              <Route path="/admin/ggrc-chain" element={<GGRCChainPage />} />
            </Route>
            
            {/* Partner Routes → Redirect to Leverancier */}
            <Route path="/partner/dashboard" element={<Navigate to="/leverancier" replace />} />
            <Route path="/partner/profile" element={<Navigate to="/leverancier/profiel" replace />} />
            <Route path="/partner/clients" element={<Navigate to="/leverancier/leads" replace />} />
            <Route path="/partner/quotes" element={<Navigate to="/leverancier/finance" replace />} />
            <Route path="/partner/insights" element={<Navigate to="/leverancier/ratings" replace />} />
            <Route path="/partner" element={<Navigate to="/leverancier" replace />} />
            
            {/* Protected Supplier Routes */}
            <Route element={<SupplierRoute />}>
              <Route path="/leverancier" element={<SupplierDashboard />} />
              <Route path="/leverancier/leads" element={<SupplierLeadsPage />} />
              <Route path="/leverancier/profiel" element={<SupplierProfilePage />} />
              <Route path="/leverancier/finance" element={<SupplierFinancePage />} />
              <Route path="/leverancier/ratings" element={<SupplierRatingsPage />} />
              <Route path="/leverancier/users" element={<SupplierUsersPage />} />
              <Route path="/leverancier/compliance" element={<SupplierCompliancePage />} />
              <Route path="/leverancier/settings" element={<SupplierSettingsPage />} />
            </Route>
            
            {/* Fallback */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        </ErrorBoundary>
        <Toaster position="top-right" duration={4000} closeButton richColors />
        </SupplierAuthProvider>
        </SubscriptionProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
